'use client'

import { cn } from '@quadralize/utils'
import { Minus, Plus } from 'lucide-react'
import type * as React from 'react'
import { Button } from '../../ui/button/button'
import { formatQuantityValue } from './quantity-input.helpers'

export interface QuantityInputProps {
  value: number
  onValueChange: (value: number) => void
  min?: number
  max?: number
  /** If true, applies the 'max' attribute to the native input element. Default: false to avoid browser native validation bubbles. */
  applyMaxAttr?: boolean
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  /** Accessible name for the decrement button. */
  decrementLabel?: string
  /** Accessible name for the increment button. */
  incrementLabel?: string
  /** Accessible name for the number input. */
  inputLabel?: string
}

export function QuantityInput({
  value,
  onValueChange,
  min = 1,
  max = 99999,
  applyMaxAttr = false,
  disabled,
  className,
  fullWidth = false,
  decrementLabel,
  incrementLabel,
  inputLabel,
}: QuantityInputProps) {
  const handleDecrement = () => {
    onValueChange(Math.max(min, value - 1))
  }

  const handleIncrement = () => {
    onValueChange(Math.min(max, value + 1))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    if (!Number.isNaN(val)) {
      onValueChange(Math.max(min, Math.min(max, val)))
    }
  }

  const handleBlur = () => {
    if (value === undefined || value === null) {
      onValueChange(min)
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1 bg-bg-elevated rounded-lg p-1.5 border border-border-default h-12 md:h-11 transition-all focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
        fullWidth ? 'w-full' : 'w-fit',
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleDecrement}
        aria-label={decrementLabel}
        className="h-11 w-11 md:h-9 md:w-9 text-text-secondary hover:text-text-primary shrink-0"
        disabled={disabled || value <= min}
      >
        <Minus className="h-4 w-4" strokeWidth={3} />
      </Button>

      <input
        type="number"
        value={formatQuantityValue(value)}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(
          'text-center bg-transparent border-none outline-none focus:outline-none text-base font-bold text-text-primary focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          fullWidth ? 'w-full' : 'w-16',
        )}
        min={min}
        max={applyMaxAttr ? max : undefined}
        disabled={disabled}
        aria-label={inputLabel}
      />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleIncrement}
        aria-label={incrementLabel}
        className="h-11 w-11 md:h-9 md:w-9 text-text-secondary hover:text-text-primary shrink-0"
        disabled={disabled || value >= max}
      >
        <Plus className="h-4 w-4" strokeWidth={3} />
      </Button>
    </div>
  )
}
